import {
  Box,
  Button,
  Grid,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { UserCommonStyles } from "../../styles/CommonStyles";
import LeftSidebar from "../../components/sidebars/LeftSidebar";
import { useTranslation } from "react-i18next";
import { CommonButton } from "../../stories/Button";
import CrossIcon from "../../images/cross.png";
import { styled } from "@mui/material/styles";
import SocialIconSection from "../../components/sidebars/SocialIconSection";
import { isEmpty } from "../../utils/helper";
import { GooglePlacesDetail } from "../../redux/action-creators/profileAction";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

const interestNameRegex = /^(?=.*[a-zA-Z])[a-zA-Z0-9\s&()\-,'.]+$/;
const CustomizedSlider = styled(Slider)`
  color: #01b4bc;
  height: 8px;

  & .MuiSlider-thumb {
    border-radius: 50px;
    color: #fff;
    width: 22px;
    height: 22px;
  }

  & .MuiSlider-rail {
    // border-radius: 50px;
    background-color: #bdbdbd;
    height: 10px;
  }
`;

function valuetext(value: number) {
  return `${value}°C`;
}

const minDistance = 10;

const EditAudience: React.FC<any> = (props: any) => {
  const {
    gender,
    setGender,
    minAndMaxAgeSlider,
    setMinAndMaxAgeSlider,
    locationArray,
    setLocationArray,
    interestArray,
    setInterestArray,
    setFormHandler,
  } = props;

  const commonStyle = UserCommonStyles();
  const { t: translation } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const [locationPredictions, setLocationPredictions] = useState<any>({
    predictions: [],
    location: "",
  });
  const [timeOutState, setTimeOutState] = useState<any>(null);
  const [interestValue, setInterestValue] = useState<string>("");
  const [validationErrors, setValidationErrors] = useState<any>({
    genderError: "",
    minAndMaxAgeError: "",
    locationError: "",
    interestError: "",
  });

  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef);

  function useOutsideAlerter(ref) {
    useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setLocationPredictions((prevState) => ({
            ...prevState,
            predictions: [],
          }));
        }
      }

      /**
       * Bind the event listener
       */
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        /**
         * Unbind the event listener on clean up
         */
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  const ageSlideHandler = (
    event: Event,
    newValue: number | number[],
    activeThumb: number
  ) => {
    if (!Array.isArray(newValue)) {
      return;
    }
    if (newValue[1] - newValue[0] < minDistance) {
      if (activeThumb === 0) {
        const clamped = Math.min(newValue[0], 100 - minDistance);
        setMinAndMaxAgeSlider([clamped, clamped + minDistance]);
      } else {
        const clamped = Math.max(newValue[1], minDistance);
        setMinAndMaxAgeSlider([clamped - minDistance, clamped]);
      }
    } else {
      setMinAndMaxAgeSlider(newValue as number[]);
    }
  };

  /**
   * Autocomplete search
   * @param text
   * @returns
   */
  const googleAutocomplete = async (text: string) => {
    if (isEmpty(text)) {
      setLocationPredictions((prevState) => ({
        ...prevState,
        predictions: [],
      }));
      return null;
    }
    if (!text) {
      return translation("valid_text");
    }
    if (typeof window === "undefined") {
      return translation("valid_window");
    }

    try {
      let result;
      let autocompleteService =
        await new google.maps.places.AutocompleteService();
      result = await autocompleteService.getPlacePredictions(
        { input: text },
        result
      );
      return result;
    } catch (e) {
      return e;
    }
  };

  /**
   * Select location
   * @param event
   */
  const selectLocation = async (event?: React.SyntheticEvent | Event) => {
    const target = event.target as HTMLInputElement;
    setLocationPredictions((prevState) => ({
      ...prevState,
      location: target.value,
    }));
    if (timeOutState) {
      clearInterval(timeOutState);
    }
    let result = setTimeout(async () => {
      let results = await googleAutocomplete(target.value);
      if (results) {
        setLocationPredictions((prevState) => ({
          ...prevState,
          predictions: results?.predictions,
        }));
      }
    }, 800);

    setTimeOutState(result);
  };

  /**
   * on Place selected by placeId
   * @param placeId
   * @param placeDescription
   * @param event
   * @returns
   */
  const onPlaceSelected = async (
    placeId: string,
    placeDescription: string,
    event: React.SyntheticEvent
  ) => {
    event.preventDefault();
    if (locationArray.length !== 0) {
      const isLocationExist = locationArray.some(
        (obj) => obj.location === placeDescription
      );
      if (isLocationExist) {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth",
        });
        //    window.scrollTo({ top: 100, left: 0, behavior: 'smooth' });
        return setValidationErrors((prev) => ({
          ...prev,
          locationError: "This location has already been selected",
        }));
      }
    }
    const response: any = await dispatch(GooglePlacesDetail(placeId));
    if (!response || !response.result) {
      return;
    } else {
      const newObject = {
        location: placeDescription,
        latitude: response.result?.geometry?.location?.lat,
        longitude: response.result?.geometry?.location?.lng,
      };
      setLocationArray([...locationArray, newObject]);
      setLocationPredictions({
        predictions: [],
        location: "",
      });
      setValidationErrors((prev) => ({
        ...prev,
        locationError: "",
      }));
    }
  };

  /**
   * remove selected location
   * @param location
   * @returns
   */
  const locationRemoveHandler = (location: string) => {
    const filterData = locationArray.filter(
      (e: any) => e.location !== location
    );
    setLocationArray(filterData);
  };

  /**
   * add interest
   * @param event
   * @returns
   */
  const addInterestHandler = (event?: React.SyntheticEvent | any) => {
    event.preventDefault();

    if (interestArray.length !== 0) {
      const isInterestExist = interestArray.some(
        (obj) => obj.tags === interestValue.toLowerCase()
      );
      if (isInterestExist) {
        return setValidationErrors((prev) => ({
          ...prev,
          interestError: "This interest has already been added",
        }));
      }
    }
    if (!interestNameRegex.test(interestValue)) {
      return setValidationErrors((prev) => ({
        ...prev,
        interestError: "Invalid interest",
      }));
    }
    const newObject = { tags: interestValue.toLowerCase() };
    setInterestArray([...interestArray, newObject]);
    setInterestValue("");
    setValidationErrors((prev) => ({
      ...prev,
      interestError: "",
    }));
  };

  /**
   * remove selected interest
   * @param tag
   * @returns
   */
  const interestRemoveHandler = (tag: string) => {
    const filterData = interestArray.filter((e: any) => e.tags !== tag);
    setInterestArray(filterData);
  };

  /**
   * To submit data
   * @returns
   */
  const onSubmit = () => {
    if (gender === "") {
      setValidationErrors((prev) => ({
        ...prev,
        genderError: "Gender is required",
      }));
      window.scrollTo({ top: 100, left: 0, behavior: "smooth" });
    } else {
      setValidationErrors((prev) => ({
        ...prev,
        genderError: "",
      }));
    }
    // if (minAndMaxAgeSlider[0] === 0 || minAndMaxAgeSlider[1] === 0) {
    //     setValidationErrors(prev => ({
    //         ...prev,
    //         minAndMaxAgeError: "Min And Max age is required"
    //     }));
    //     window.scrollTo({ top: 100, left: 0, behavior: 'smooth' });
    // } else {
    //     setValidationErrors(prev => ({
    //         ...prev,
    //         minAndMaxAgeError: ""
    //     }));
    // };
    if (locationArray.length === 0) {
      setValidationErrors((prev) => ({
        ...prev,
        locationError: "Location is required",
      }));
      window.scrollTo({
        top: gender === "" ? 100 : 300,
        left: 0,
        behavior: "smooth",
      });
    } else {
      setValidationErrors((prev) => ({
        ...prev,
        locationError: "",
      }));
    }
    if (interestArray.length === 0) {
      setValidationErrors((prev) => ({
        ...prev,
        interestError: "Interest is required",
      }));
      return;
    } else {
      setValidationErrors((prev) => ({
        ...prev,
        interestError: "",
      }));
    }
    if (
      gender !== "" &&
    //   minAndMaxAgeSlider[0] !== 0 &&
    //   minAndMaxAgeSlider[1] !== 0 &&
      locationArray.length !== 0 &&
      interestArray.length !== 0
    ) {
      // const data = {
      //     locationArray,
      //     interestArray,
      //     gender,
      //     minAndMaxAgeSlider
      // };
      // console.log('Edit Audience data', data);
      setFormHandler({
        isShowStep1: false,
        isShowStep2: false,
        isShowStep3: false,
        isShowStep4: true,
      });
    }
  };

  const cancelProduct = () => {
    // setGender("");
    // setMinAndMaxAgeSlider([0, 0]);
    // setInterestArray([]);
    // setLocationArray([]);
    setFormHandler({
      isShowStep1: false,
      isShowStep2: true,
      isShowStep3: false,
      isShowStep4: false,
    });
  };

  return (
    <Box className={commonStyle.windowColor}>
      <Grid container>
        <Grid item md={3}>
          <LeftSidebar />
          {/* <SocialIconSection /> */}
        </Grid>
        <Grid item md={9}>
          <Box className={commonStyle.p20}>
            <Box className={`${commonStyle.mb15} ${commonStyle.mt30}`}>
              <Typography
                variant="h6"
                className={`${commonStyle.grey} ${commonStyle.fontWeight600} ${commonStyle.f_family_Roboto} ${commonStyle.font20}`}
              >
                Edit Audience
              </Typography>
            </Box>
            <Box pt={3} className={`${commonStyle.p20} ${commonStyle.card}`}>
              <Grid container>
                {/* Dropdown Container //////////// */}
                <Grid item lg={12} md={12} sm={12}>
                  <Typography
                    mb={0.5}
                    variant="h6"
                    className={`${commonStyle.tundora} ${commonStyle.fontWeight400} ${commonStyle.f_family_Roboto} ${commonStyle.font14}`}
                  >
                    Select the location of people you want to reach with your
                    ad.
                  </Typography>
                  <Box className={`${commonStyle.mb15} ${commonStyle.mt30}`}>
                    <Typography
                      variant="h6"
                      className={`${commonStyle.tundora} ${commonStyle.fontWeight500} ${commonStyle.f_family_Roboto} ${commonStyle.font20}`}
                    >
                      Gender
                    </Typography>
                  </Box>

                  {/* Gender Selection Container //////////  */}
                  <Box
                    className={`${commonStyle.fontWeight400} ${commonStyle.f_family_Roboto} ${commonStyle.font18}`}
                    sx={{
                      width: "85%",
                      display: "flex",
                      justifyContent: "space-around",
                      alignItems: "center",
                      marginLeft: "auto",
                      marginRight: "auto",
                      marginBottom: "15px",
                    }}
                  >
                    <Button
                      sx={{
                        maxWidth: "220px",
                        width: "100%",
                        minWidth: "150px",
                        height: "43px",
                        backgroundColor:
                          gender === "all" ? "#c7eef0" : "#e0e0e0",
                        color: gender === "all" ? "#01B4BC" : "#4C4C4C",
                        borderRadius: "8px",
                      }}
                      onClick={() => setGender("all")}
                    >
                      All
                    </Button>
                    <Button
                      sx={{
                        maxWidth: "220px",
                        width: "100%",
                        minWidth: "150px",
                        height: "43px",
                        backgroundColor:
                          gender === "men" ? "#c7eef0" : "#e0e0e0",
                        color: gender === "men" ? "#01B4BC" : "#4C4C4C",
                        borderRadius: "8px",
                      }}
                      onClick={() => setGender("men")}
                    >
                      Men
                    </Button>
                    <Button
                      sx={{
                        maxWidth: "220px",
                        width: "100%",
                        minWidth: "150px",
                        height: "43px",
                        backgroundColor:
                          gender === "women" ? "#c7eef0" : "#e0e0e0",
                        color: gender === "women" ? "#01B4BC" : "#4C4C4C",
                        borderRadius: "8px",
                      }}
                      onClick={() => setGender("women")}
                    >
                      Women
                    </Button>
                  </Box>
                  {validationErrors && validationErrors.genderError && (
                    <span className={`${commonStyle.errorMsg}`}>
                      {validationErrors.genderError}
                    </span>
                  )}
                </Grid>

                {/* Age Limit Section ////////// */}
                {/* <Grid item lg={12} md={12} sm={12}>
                                    <Box mt={8} mb={5}>
                                        <Typography
                                            variant="h6"
                                            className={`${commonStyle.tundora} ${commonStyle.fontWeight500} ${commonStyle.f_family_Roboto} ${commonStyle.font20}`}
                                        >
                                            Age
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        width: "92%",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: "30px",
                                        marginLeft: "auto",
                                        marginRight: "auto",
                                        marginBottom: "15px"
                                    }}>
                                        <Typography
                                            variant="h6"
                                            className={`${commonStyle.silver_Chalice} ${commonStyle.fontWeight500} ${commonStyle.f_family_Roboto} ${commonStyle.font20}`}
                                        >
                                            {minAndMaxAgeSlider[0]}
                                        </Typography>

                                        <CustomizedSlider
                                            getAriaLabel={() => 'Minimum distance shift'}
                                            value={minAndMaxAgeSlider}
                                            min={18}
                                            onChange={ageSlideHandler}
                                            valueLabelDisplay="auto"
                                            getAriaValueText={valuetext}
                                        // disableSwap
                                        />
                                        <Typography
                                            variant="h6"
                                            className={`${commonStyle.silver_Chalice} ${commonStyle.fontWeight500} ${commonStyle.f_family_Roboto} ${commonStyle.font20}`}
                                        >
                                            {`${minAndMaxAgeSlider[1]}`}
                                        </Typography>
                                    </Box>
                                    {validationErrors && validationErrors.minAndMaxAgeError && (
                                        <span className={`${commonStyle.errorMsg}`}>
                                            {validationErrors.minAndMaxAgeError}
                                        </span>
                                    )}
                                </Grid> */}

                {/* Location Target Section //////////////// */}
                <Grid item lg={12} md={12} sm={12} mt={6} mr={6}>
                  <Typography
                    mb={0.5}
                    variant="h6"
                    className={`${commonStyle.tundora} ${commonStyle.fontWeight500} ${commonStyle.f_family_Roboto} ${commonStyle.font20}`}
                  >
                    Location
                  </Typography>
                  <Box
                    pt={3}
                    pb={1.8}
                    pl={3}
                    pr={3}
                    mb={0.5}
                    className={commonStyle.box_border_searchLocation}
                  >
                    <TextField
                      id="standard-basic"
                      sx={{
                        width: "100%",
                        height: "50px",
                        marginBottom: "10px",
                      }}
                      placeholder="Type to add location"
                      variant="standard"
                      onChange={selectLocation}
                      value={locationPredictions.location}
                    />
                    {locationPredictions.predictions.length !== 0 && (
                      <div className="search-drop-list" ref={wrapperRef}>
                        <ul className={commonStyle.searchCommonList}>
                          {locationPredictions.predictions.map((prediction) => {
                            return (
                              <li
                                key={prediction?.place_id}
                                onClick={(e) =>
                                  onPlaceSelected(
                                    prediction?.place_id,
                                    prediction?.description,
                                    e
                                  )
                                }
                              >
                                {prediction?.description || "Not Found"}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                    {locationArray.length !== 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          gap: "30px",
                          flexWrap: "wrap",
                        }}
                      >
                        {locationArray
                          .toReversed()
                          .map((ele: any, index: number) => {
                            return (
                              <Box
                                key={index}
                                pt={0.7}
                                pl={1}
                                pr={1}
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "center",
                                  gap: "13px",
                                  backgroundColor: "#c7eef0",
                                  borderRadius: "2px",
                                }}
                              >
                                <Typography
                                  mb={0.5}
                                  variant="h6"
                                  className={`${commonStyle.skyBlue} ${commonStyle.fontWeight400} ${commonStyle.f_family_Roboto} ${commonStyle.font14}`}
                                >
                                  {ele?.location ? ele?.location : ""}
                                </Typography>
                                <img
                                  src={CrossIcon}
                                  alt=""
                                  style={{
                                    width: "10px",
                                    height: "10px",
                                    cursor: "pointer",
                                    marginTop: -5,
                                  }}
                                  onClick={() =>
                                    locationRemoveHandler(ele?.location)
                                  }
                                />
                              </Box>
                            );
                          })}
                      </Box>
                    )}
                  </Box>
                  {validationErrors && validationErrors.locationError && (
                    <span className={`${commonStyle.errorMsg}`}>
                      {validationErrors.locationError}
                    </span>
                  )}
                </Grid>

                {/* Interest Target Section //////////////// */}
                <Grid item lg={12} md={12} sm={12} mt={6} mr={6}>
                  <Typography
                    mb={0.5}
                    variant="h6"
                    className={`${commonStyle.tundora} ${commonStyle.fontWeight500} ${commonStyle.f_family_Roboto} ${commonStyle.font20}`}
                  >
                    Detailed targeting
                  </Typography>
                  <Box
                    pt={3}
                    pb={1.8}
                    pl={3}
                    pr={3}
                    mb={0.5}
                    className={commonStyle.box_border_searchLocation}
                    sx={{ position: "relative" }}
                  >
                    {interestValue !== "" && (
                      <Button
                        variant="text"
                        sx={{
                          position: "absolute",
                          right: 27,
                          top: 10,
                          zIndex: 999,
                          textTransform: "capitalize",
                        }}
                        onClick={addInterestHandler}
                      >
                        Add
                      </Button>
                    )}

                    <form onSubmit={addInterestHandler}>
                      <TextField
                        id="standard-basic"
                        sx={{
                          width: "100%",
                          height: "50px",
                          marginBottom: "10px",
                        }}
                        placeholder="Search interest"
                        variant="standard"
                        onChange={(event) =>
                          setInterestValue(event.target.value)
                        }
                        value={interestValue}
                      />
                    </form>
                    {interestArray.length !== 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          gap: "30px",
                          flexWrap: "wrap",
                        }}
                      >
                        {interestArray
                          .toReversed()
                          .map((ele: any, index: number) => {
                            return (
                              <Box
                                pt={0.7}
                                pl={1}
                                pr={1}
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "center",
                                  gap: "13px",
                                  backgroundColor: "#c7eef0",
                                  borderRadius: "2px",
                                }}
                              >
                                <Typography
                                  mb={0.5}
                                  variant="h6"
                                  sx={{ textTransform: "capitalize" }}
                                  className={`${commonStyle.skyBlue} ${commonStyle.fontWeight400} ${commonStyle.f_family_Roboto} ${commonStyle.font14}`}
                                >
                                  {ele?.tags ? ele?.tags : ""}
                                </Typography>
                                <img
                                  src={CrossIcon}
                                  alt=""
                                  style={{
                                    width: "10px",
                                    height: "10px",
                                    cursor: "pointer",
                                    marginTop: -5,
                                  }}
                                  onClick={() =>
                                    interestRemoveHandler(ele?.tags)
                                  }
                                />
                              </Box>
                            );
                          })}
                      </Box>
                    )}
                  </Box>
                  {validationErrors && validationErrors.interestError && (
                    <span className={`${commonStyle.errorMsg}`}>
                      {validationErrors.interestError}
                    </span>
                  )}
                </Grid>

                <Grid item lg={12} md={12} sm={12} mt={7} mb={5}>
                  <Box className={commonStyle.flexCenter}>
                    <Box
                      className={`${commonStyle.mr10} ${commonStyle.blueBtn}`}
                    >
                      <Button onClick={onSubmit}>{"Save & Continue"}</Button>
                    </Box>
                    <Box
                      className={`${commonStyle.mr10} ${commonStyle.greyBtn}`}
                    >
                      <CommonButton onClick={cancelProduct} label={"Cancel"} />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EditAudience;
